import { useMutation, useQueryClient } from "@tanstack/react-query";
import { usePagedQuery } from "./use-paged-query";
import { api } from "./api-client";

export type CampaignStatus =
    | "DRAFT"
    | "SCHEDULED"
    | "RUNNING"
    | "PAUSED"
    | "COMPLETED"
    | "CANCELLED"
    | "FAILED";

export type CampaignMedia = {
    id: string;
    kind: string;
    filename: string;
    url: string;
    thumbnailUrl: string | null;
    order: number;
};

export type Campaign = {
    id: string;
    accountId: string;
    accountName: string;
    name: string;
    status: CampaignStatus;
    messageText: string;
    mode: "SEQUENTIAL" | "PARALLEL";
    concurrency: number | null;
    minDelayMs: number | null;
    maxDelayMs: number | null;
    scheduleAt: string | null;
    timezone: string;
    groupSetIds: string[];
    groupIds: string[];
    media: CampaignMedia[];
    targetCount: number;
    lastRun: {
        id: string;
        status: string;
        totalCount: number;
        sentCount: number;
        failedCount: number;
        uncertainCount: number;
        finishedAt: string | null;
    } | null;
    createdAt: string;
};

export type CampaignInput = {
    accountId: string;
    name: string;
    messageText: string;
    mode: "SEQUENTIAL" | "PARALLEL";
    groupSetIds: string[];
    groupIds: string[];
    mediaIds: string[];
};

const KEY = "campaigns";

export function useCampaigns(accountId?: string) {
    return usePagedQuery<Campaign>({
        queryKey: [KEY, accountId],
        path: `/api/campaigns${accountId ? `?accountId=${accountId}` : ""}`,
    });
}

function useCampaignMutation<TVariables, TResult>(fn: (variables: TVariables) => Promise<TResult>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
    });
}

export function useCreateCampaign() {
    return useCampaignMutation((body: CampaignInput) => api.post<Campaign>("/api/campaigns", body));
}

export function useUpdateCampaign() {
    return useCampaignMutation(({ id, ...body }: Partial<CampaignInput> & { id: string }) =>
        api.patch<Campaign>(`/api/campaigns/${id}`, body),
    );
}

export function useDeleteCampaign() {
    return useCampaignMutation((id: string) => api.delete(`/api/campaigns/${id}`));
}

export function useRunCampaign() {
    return useCampaignMutation((id: string) =>
        api.post<{ runId: string; taskCount: number }>(`/api/campaigns/${id}/run`),
    );
}

export function useScheduleCampaign() {
    return useCampaignMutation(({ id, scheduleAt }: { id: string; scheduleAt: string }) =>
        api.post<Campaign>(`/api/campaigns/${id}/schedule`, { scheduleAt }),
    );
}

export function useCancelSchedule() {
    return useCampaignMutation((id: string) => api.post<Campaign>(`/api/campaigns/${id}/cancel-schedule`));
}

